import {DateTime} from 'luxon';

const MS_PER_DAY = 86_400_000;

// Chart timestamps are UTC date-only values (both charts key off midnight-UTC
// dates), so all calendar math — boundaries and the labels themselves — runs
// in UTC. Doing it in the browser's local zone would, west of UTC, read a
// midnight-UTC point as the previous day and drop the final year's tick/label.
const UTC = {zone: 'utc'} as const;

/**
 * Pick an axis date format from the span the chart actually covers, so tick
 * labels are never ambiguous. A bare "Jun 9" hides which year it belongs to,
 * which is misleading on a chart that spans years. Instead:
 *
 * - spans over ~2 years label ticks with the year alone ("2024"),
 * - spans of a few months to ~2 years use "MMM yyyy" ("Jan 2026"),
 * - shorter windows keep day-level detail ("Jun 9").
 *
 * Pair with {@link niceTimeTicks} so year-granularity labels land on year
 * boundaries and never repeat.
 */
export const spanDateFormatter =
  (minTs: number, maxTs: number) =>
  (ts: number): string => {
    const spanDays = Math.abs(maxTs - minTs) / MS_PER_DAY;
    const dt = DateTime.fromMillis(ts, UTC);
    if (spanDays > 750) {
      return dt.toFormat('yyyy');
    }
    if (spanDays > 90) {
      return dt.toFormat('MMM yyyy');
    }
    return dt.toFormat('MMM d');
  };

/**
 * Calendar-aligned tick timestamps for a time axis spanning [minTs, maxTs], at
 * a granularity that matches the span (years for multi-year spans, months for
 * multi-month, days otherwise) and thinned to a handful of labels. Aligning to
 * calendar boundaries keeps the labels {@link spanDateFormatter} produces
 * clean — one tick per year means the year-only labels never collide, which
 * auto-placed ticks (landing on arbitrary instants) cannot guarantee.
 */
export const niceTimeTicks = (minTs: number, maxTs: number): number[] => {
  if (!(maxTs > minTs)) {
    return [minTs];
  }

  const spanDays = (maxTs - minTs) / MS_PER_DAY;
  const start = DateTime.fromMillis(minTs, UTC);
  const end = DateTime.fromMillis(maxTs, UTC);
  const ticks: number[] = [];

  // Walk calendar boundaries from the first aligned point at or after the
  // start; boundaries before the start would be clipped by the axis domain.
  const collect = (first: DateTime, advance: (dt: DateTime) => DateTime) => {
    for (let cur = first; cur <= end; cur = advance(cur)) {
      if (cur >= start) {
        ticks.push(cur.toMillis());
      }
    }
  };

  if (spanDays > 750) {
    const years = end.year - start.year + 1;
    const step = Math.max(1, Math.ceil(years / 8));
    collect(start.startOf('year'), (dt) => dt.plus({years: step}));
  } else if (spanDays > 90) {
    const months =
      Math.round(end.diff(start.startOf('month'), 'months').months) + 1;
    const step = Math.max(1, Math.ceil(months / 7));
    collect(start.startOf('month'), (dt) => dt.plus({months: step}));
  } else {
    const step = Math.max(1, Math.ceil(spanDays / 6));
    collect(start.startOf('day'), (dt) => dt.plus({days: step}));
  }

  return ticks.length > 0 ? ticks : [minTs];
};
